/**
 * ClassifierExplain — inline "why did this get this class?" component
 * used by the Material Table (Auto-Rule editor) and the Block Properties
 * panel to expose the classifier's reasoning without opening a modal.
 *
 * Session I-6 (approved same session) — three of the last four SSoT
 * bugs (mini-card ore, G-T naive ore, name-gated classifier) shared a
 * root cause: multiple hidden code paths for what should be one
 * canonical answer. This component surfaces the CANONICAL decision
 * ("grade X compared to tier hg/mg/lg/marginal", "material class →
 * kind", "no assay coverage → name-hint fallback") right next to the
 * numeric result, so QA can hover to see the reasoning instead of
 * grepping backend code.
 *
 * Also exposes MATERIAL_CLASS_KIND — the ONE canonical map from class
 * code → engineering kind (ore | waste | overburden). Callers that
 * need to display Ore(t) vs Waste(t) for a single block/interval
 * should import this rather than reimplementing the taxonomy.
 */
import { Info } from "lucide-react";

/**
 * Canonical class-code → kind lookup. MUST stay in sync with
 * `backend/material_classes.py` DEFAULT_CLASSES + `_OVERBURDEN_CODES`.
 * When adding a new class server-side, add it here too — nothing in the
 * UI should ever guess a kind from a code.
 */
export const MATERIAL_CLASS_KIND = {
  // Ore family
  hg_ore:      "ore",
  mg_ore:      "ore",
  lg_ore:      "ore",
  marginal:    "ore",
  oxide:       "ore",
  sulphide:    "ore",
  mixed:       "ore",
  // Overburden family (rolls into total waste tonnage per Master Spec §1)
  topsoil:     "overburden",
  overburden:  "overburden",
  saprolite:   "overburden",
  // Waste family
  transition:  "waste",
  fresh_rock:  "waste",
  waste:       "waste",
  unclassified:"waste",
};

/**
 * Return { ore, waste, overburden } tonnages for a single block, given
 * its `material_class` + `tonnage`. Called by PropertiesPanel so the
 * "Ore(t) / Waste(t)" row on a single-block selection matches the
 * class_summary that every dashboard reads.
 */
export function splitBlockTonnage(block) {
  if (!block) return { ore: 0, waste: 0, overburden: 0 };
  const t = Number(block.tonnage || 0);
  if (!Number.isFinite(t) || t === 0) return { ore: 0, waste: 0, overburden: 0 };
  const code = block.material_class || block.rock_type || null;
  const kind = code ? (MATERIAL_CLASS_KIND[code] || null) : null;
  if (kind === "ore")        return { ore: t,  waste: 0,  overburden: 0 };
  if (kind === "overburden") return { ore: 0,  waste: t,  overburden: t };
  if (kind === "waste")      return { ore: 0,  waste: t,  overburden: 0 };
  // Unknown / unclassified — fall through to legacy grade-vs-cutoff hint
  // when the block carries a cutoff_grade reference. Otherwise mark waste.
  if (block.cutoff_grade != null && block.grade != null) {
    return block.grade >= block.cutoff_grade
      ? { ore: t, waste: 0, overburden: 0 }
      : { ore: 0, waste: t, overburden: 0 };
  }
  return { ore: 0, waste: t, overburden: 0 };
}

/**
 * Compact inline "reasoning" hover chip. Used inside table rows or next
 * to a numeric readout. Renders an info icon + native `title` attr for
 * OS-provided tooltip. Callers pass the reason text — the component
 * doesn't try to compute reasoning itself, it just surfaces whatever
 * the SSoT source computed.
 *
 * Kept intentionally minimal (no Radix Tooltip provider dependency) so
 * this can drop in ANYWHERE without provider setup — including deeply
 * nested panels, popovers, and dialogs.
 */
export default function ClassifierExplain({
  reason, source = "ssot", size = "sm", className = "", "data-testid": testId,
}) {
  if (!reason) return null;
  const px = size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5";
  return (
    <span
      className={`inline-flex items-center gap-0.5 cursor-help align-middle ${className}`}
      title={reason}
      data-ssot-source={source}
      data-testid={testId}
    >
      <Info className={`${px} opacity-60`} strokeWidth={1.75} />
    </span>
  );
}

/**
 * Larger explanation block (not just a hover chip) — for Properties panel
 * groups where we have vertical space to show the classifier reasoning
 * inline. Wraps `reason` in a muted callout with the source pill.
 */
export function ClassifierExplainBlock({ reason, source = "ssot", className = "", "data-testid": testId }) {
  if (!reason) return null;
  const sourceLabel = {
    ssot:     "Material class",
    grade:    "Grade cutoff",
    legacy:   "Legacy",
    external: "External",
  }[source] || source;
  return (
    <div
      className={`text-[10px] font-mono leading-relaxed border-l-2 border-primary/50 pl-2 my-1 ${className}`}
      data-ssot-source={source}
      data-testid={testId}
    >
      <span className="uppercase tracking-widest text-[9px] text-primary mr-1">{sourceLabel}</span>
      <span className="opacity-80">{reason}</span>
    </div>
  );
}
